/**
 * LoadMoreBar — bottom-of-list pager.
 *
 * Renders "Načteno X z Y" + an optional "Načíst dalších N" button. Used by
 * every list page that pulls data via TanStack `useInfiniteQuery`. When the
 * list has loaded everything, the button hides and the text reads
 * "Načteno všech Y …".
 *
 * Wire it underneath the `<DataTable>` and call `onLoadMore()` from the click
 * handler — the parent owns the query and decides what "next" means.
 */

import type { ReactNode } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';
import { formatInt } from '@/lib/format';

export interface LoadMoreBarProps {
  /** Items currently visible (after flattening pages). */
  loaded: number;
  /** Server-reported total matching the current filters. */
  total: number;
  /** Whether there's another page to fetch. */
  hasMore: boolean;
  /** Click handler — usually `fetchNextPage` from useInfiniteQuery. */
  onLoadMore: () => void;
  /** While the next page is in flight, button is disabled and shows "Načítám…". */
  loading?: boolean;
  /** Default page size shown in the button label. */
  pageSize?: number;
  /** Override the units in the count text. Defaults to "záznamů". */
  unit?: string;
  /** Optional extra content rendered to the right of the count (e.g. a sub-hint). */
  trailing?: ReactNode;
  className?: string;
}

export function LoadMoreBar({
  loaded,
  total,
  hasMore,
  onLoadMore,
  loading = false,
  pageSize = 300,
  unit = 'záznamů',
  trailing,
  className = '',
}: LoadMoreBarProps) {
  if (total === 0) return null;
  const remaining = Math.max(0, total - loaded);
  const nextBatch = Math.min(pageSize, remaining);

  return (
    <div
      data-testid="load-more-bar"
      className={
        'flex items-center gap-4 border-t border-line bg-paper px-8 py-3 ' +
        className
      }
    >
      <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        {hasMore ? (
          <>
            Načteno {formatInt(loaded)} z {formatInt(total)} {unit}
          </>
        ) : (
          <>
            Načteno všech {formatInt(total)} {unit}
          </>
        )}
      </span>
      {trailing}
      <div className="flex-1" />
      {hasMore && (
        <Button
          variant="secondary"
          size="sm"
          icon={<Icon name="chevronD" size={12} />}
          onClick={onLoadMore}
          disabled={loading}
        >
          {loading ? 'Načítám…' : `Načíst dalších ${formatInt(nextBatch)}`}
        </Button>
      )}
    </div>
  );
}
